import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Botão do painel.
 *
 * O tema padrão do shadcn/ui é sobrescrito de propósito: o botão primário é
 * AMARELO (`--primary` = #EBCD09) com TEXTO PRETO (`--primary-foreground` =
 * #000000). Amarelo sobre preto passa AAA; amarelo com texto branco reprova em
 * qualquer nível. Nenhum hex aparece aqui — as duas variáveis vêm de
 * `@locaveri/ui/theme.css`.
 *
 * Amarelo é acento: use `default` só no CTA principal da tela.
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-brand-400 active:bg-brand-600',
        secondary: 'border border-border bg-secondary text-secondary-foreground hover:bg-elevated',
        outline:
          'border border-border-strong bg-transparent hover:bg-accent hover:text-accent-foreground',
        ghost: 'text-fg-secondary hover:bg-accent hover:text-foreground',
        link: 'text-brand underline-offset-4 hover:underline',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        default: 'h-10 px-4 py-2',
        lg: 'h-11 px-6 text-base',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Renderiza o filho no lugar do `<button>`, mantendo os estilos. */
  readonly asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className)

    if (asChild) {
      return <Slot ref={ref} data-slot="button" className={classes} {...props} />
    }

    return (
      <button
        ref={ref}
        data-slot="button"
        className={classes}
        // Sem `type` explícito o botão submeteria o formulário sem querer.
        type={type ?? 'button'}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
